import debugTools from "./debug";

/**
 * Localstorage should have abbaTargets and abbaExperiments
 * Both are stringified objects
 * abbaTargets maps targetIds to experiment and bucket ids.
 * They will only exist in this object if the user was chosen to participate in an experiment.
 * abbaTargets: { [targetId] : { experimentId, bucketId } }
 * abbaExperiments maps experimentId to bucketId and end date.
 * There will be a key for each experimentId as they are processed.
 * If the user was chosen to not participate in the experiment, experimentId and bucketId will be null.
 * abbaExperiments: { [experimentId]: { bucketId, endTime, locked } }
 * abbaPreview is used for persistence in preview.
 * It stores the last preview config and will continue to apply it until a new one is set or 30 mins have passed.
 * abbaPreview: { setAt, config }
 */

let bridge = null;
const setBridge = (platformBridge) => {
    bridge = platformBridge;
};
const checkBridge = () => {
    if (bridge === null) {
        throw new Error("Bridge is not set.");
    }
};

const TARGETS_KEY = "abbaTargets";
const EXPERIMENTS_KEY = "abbaExperiments";
const PREVIEW_KEY = "abbaPreview";
const PREVIEW_TTL = 1000 * 60 * 30; // 30 minutes

const getTargetsState = async () => {
    checkBridge();
    const exists = await bridge.getItem(TARGETS_KEY);

    if (exists !== undefined && exists !== null) {
        return exists;
    } else {
        const emptyConfig = {};

        await bridge.setItem(TARGETS_KEY, emptyConfig);

        return emptyConfig;
    }
};

const getExperimentsState = async () => {
    const exists = await bridge.getItem(EXPERIMENTS_KEY);

    if (exists !== undefined && exists !== null) {
        return exists;
    } else {
        const emptyConfig = {};

        await bridge.setItem(EXPERIMENTS_KEY, emptyConfig);

        return emptyConfig;
    }
};

const getTargetPlacement = async (targetId) => {
    const targetsState = await getTargetsState();

    return targetsState[targetId];
};

const getExperimentPlacement = async (experimentId) => {
    const experimentsState = await getExperimentsState();

    return experimentsState[experimentId];
};

const getPreview = async () => {
    checkBridge();
    const exists = bridge.getItem(PREVIEW_KEY);

    if (exists !== undefined && exists !== null) {
        const previewSetAt = new Date(exists.setAt);
        const now = new Date();

        if (now - previewSetAt >= PREVIEW_TTL) {
            debugTools.info("Preset preview has expired. Removing preview.");
            bridge.setItem(PREVIEW_KEY, "undefined");
        } else {
            return exists.config;
        }
    }

    return false;
};

const setPreview = async (config) => {
    checkBridge();
    debugTools.info("Setting detected preview", config);
    await bridge.setItem(PREVIEW_KEY, {
        config,
        setAt: new Date().toISOString(),
    });
};

const setTargetPlacement = async (targetId, experimentId, bucketId) => {
    const currentState = await getTargetsState();
    const assignedDate = currentState[targetId]?.assignedDate;

    currentState[targetId] = {
        experimentId,
        bucketId,
        assignedDate: assignedDate ?? new Date(),
    };
    await bridge.setItem(TARGETS_KEY, currentState);
};

const setExperimentPlacement = async (experimentId, bucketId, endTime, locked) => {
    const currentState = await getExperimentsState();
    let assignedDate;

    // Use previously assignedDate field if the user is in the same bucket still
    if (currentState[experimentId] && currentState[experimentId].bucketId === bucketId) {
        assignedDate = currentState[experimentId].assignedDate;
    }

    currentState[experimentId] = {
        bucketId,
        assignedDate: assignedDate ?? new Date(),
        endTime,
        locked,
    };
    await bridge.setItem(EXPERIMENTS_KEY, currentState);
};

const removeTargetPlacement = async (targetId) => {
    const currentState = await getTargetsState();

    if (currentState[targetId]) {
        delete currentState[targetId];
        await bridge.setItem(TARGETS_KEY, currentState);
    }
};

const removeExperimentPlacement = async (experimentId) => {
    const currentState = await getExperimentsState();

    if (currentState[experimentId]) {
        delete currentState[experimentId];
        await bridge.setItem(EXPERIMENTS_KEY, currentState);
    }
};

const updateExperimentEndTimes = async (target) => {
    if (target && target.experiments && target.experiments.length) {
        let e;

        for (let i = 0; i < target.experiments.length; i++) {
            e = target.experiments[i];
            const exists = await getExperimentPlacement(e.experimentId);

            if (exists && exists.endTime !== e.endTime) {
                await setExperimentPlacement(e.experimentId, exists.bucketId, e.endTime, e.locked);
            }
        }
    }

    await removeExpiredExperiments(target);
};

const removeExpiredExperiments = async (target) => {
    const currentState = await getExperimentsState();
    const existingExperiments = Object.keys(currentState);
    let k;

    for (let i = 0; i < existingExperiments.length; i++) {
        k = existingExperiments[i];
        const existingExperiment = currentState[k];
        const expires = new Date(existingExperiment.endTime);

        if (expires < new Date() || !(await experimentPlacementValid(k, target))) {
            if (existingExperiment.bucketId !== null) {
                await removeTargetPlacement(target.targetId);
            }

            await removeExperimentPlacement(k);
        }
    }
};

const experimentPlacementValid = async (experimentId, target) => {
    const currTargetPlace = await getTargetPlacement(target.targetId);

    if (currTargetPlace && currTargetPlace.experimentId === experimentId) {
        // localstorage says the user belongs in the experiment for this target.
        if (!experimentFoundInTargets(experimentId, target)) {
            return false;
        }
    }

    return true;
};

const experimentFoundInTargets = (experimentId, target) => {
    const foundExperiment = target.experiments.filter((e) => e.experimentId === experimentId);

    if (foundExperiment.length) {
        return true;
    }

    return false;
};

const geoIP = async (userInfo) => {
    return userInfo.geoIP;
};

const lockedInExperiment = async (target) => {
    const currTargetPlacement = await getTargetPlacement(target.targetId);

    if (currTargetPlacement && currTargetPlacement.experimentId) {
        const currExpPlacement = await getExperimentPlacement(currTargetPlacement.experimentId);

        if (currExpPlacement && currExpPlacement.locked) {
            return true;
        }
    }

    return false;
};

const clearAbbaStorage = async () => {
    checkBridge();
    await bridge.setItem(TARGETS_KEY, {});
    await bridge.setItem(EXPERIMENTS_KEY, {});
    debugTools.info("Abba storage cleared. Refresh to be rebucketed.");
};

debugTools.setClearAbbaStorage(clearAbbaStorage);

export default {
    geoIP,
    checkBridge,
    setBridge,
    getTargetPlacement,
    setTargetPlacement,
    removeTargetPlacement,
    getExperimentPlacement,
    setExperimentPlacement,
    removeExperimentPlacement,
    updateExperimentEndTimes,
    lockedInExperiment,
    getPreview,
    setPreview,
    clearAbbaStorage,
};
